type AutomationIdCandidate = string | null | undefined;

export interface AutomationNavDescriptor {
	automationKey?: AutomationIdCandidate;
	route?: AutomationIdCandidate;
	label?: AutomationIdCandidate;
	id?: AutomationIdCandidate;
	parentSegment?: AutomationIdCandidate;
}

/**
 * Normaliza um texto em um segmento estável para IDs de automação (remove query/fragment de URL,
 * remove acentos, minúsculas, substitui sequências não alfanuméricas por hífen e remove hífens nas pontas).
 *
 * @param value - Texto bruto ou vazio; valores falsy retornam `undefined`
 * @returns Segmento normalizado ou `undefined` se o resultado for vazio
 */
export const toAutomationIdSegment = (value: AutomationIdCandidate): string | undefined => {
	if (!value) return undefined;

	const segment = value
		.replace(/[?#].*$/, '')          // remove query string e fragment de URLs (?foo=bar, #anchor)
		.normalize('NFD')                 // decompõe letras acentuadas em letra base + acento separados
		.replace(/[\u0300-\u036f]/g, '') // remove os acentos (diacríticos) que foram separados acima
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')    // substitui qualquer sequência de caracteres inválidos por hífen
		.replace(/^-|-$/g, '');          // remove hífens no início e fim

	return segment || undefined;
};

/**
 * Aplica a mesma normalização de {@link toAutomationIdSegment} a um caminho de rota.
 *
 * @param route - Rota ou valor vazio
 * @returns Segmento normalizado ou `undefined` se não houver segmento válido
 */
export const routeToAutomationIdSegment = (route: AutomationIdCandidate): string | undefined => {
	return toAutomationIdSegment(route);
};

/**
 * Percorre os valores na ordem e devolve o primeiro segmento não vazio após normalização.
 *
 * @param values - Candidatos testados em sequência até o primeiro hit
 * @returns Primeiro segmento definido ou `undefined`
 */
export const resolveAutomationIdSegment = (
	...values: AutomationIdCandidate[]
): string | undefined => {
	for (const value of values) {
		const segment = toAutomationIdSegment(value);
		if (segment) return segment;
	}

	return undefined;
};

/**
 * Resolve o segmento de automação para itens de navegação: prioridade `automationKey`, depois `route`,
 * depois `label`/`id`; se existir `parentSegment`, o resultado é `parentSegment-itemSegment`.
 *
 * @param descriptor - Chaves opcionais do item de nav (`AutomationNavDescriptor`)
 * @returns Segmento composto ou `undefined` quando nada puder ser resolvido
 */
export const resolveNavAutomationIdSegment = ({
	automationKey,
	route,
	label,
	id,
	parentSegment,
}: AutomationNavDescriptor): string | undefined => {
	const automationKeySegment = toAutomationIdSegment(automationKey);
	if (automationKeySegment) return automationKeySegment;

	const routeSegment = routeToAutomationIdSegment(route);
	if (routeSegment) return routeSegment;

	const itemSegment = resolveAutomationIdSegment(label, id);
	if (!itemSegment) return undefined;
	if (!parentSegment) return itemSegment;

	return `${parentSegment}-${itemSegment}`;
};

/**
 * Predicado de type guard: mantém apenas candidatos truthy como `string` (útil em `Array.prototype.filter`).
 *
 * @param segment - Candidato a segmento
 * @returns `true` se for string não vazia
 */
const isTruthyAutomationSegment = (segment: AutomationIdCandidate): segment is string =>
	!!segment;

/**
 * Monta um ID final `base-seg1-seg2-...`, ignorando segmentos vazios ou ausentes.
 *
 * @param base - Prefixo obrigatório; se falsy, retorna `undefined`
 * @param segments - Segmentos opcionais concatenados após `base`
 * @returns String com partes unidas por `-` ou `undefined` se `base` for inválido
 */
export const buildAutomationId = (
	base: AutomationIdCandidate,
	...segments: AutomationIdCandidate[]
): string | undefined => {
	if (!base) return undefined;

	const filtered = segments.filter(isTruthyAutomationSegment);
	return [base, ...filtered].join('-');
};
